import { useState, useEffect } from 'react';
import { useSessionContext } from '@supabase/auth-helpers-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2, ChevronRight, Gauge } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { format, differenceInDays } from 'date-fns';
import { useTranslation } from "react-i18next";

export const SubscriptionManagement = () => {
  const { session } = useSessionContext();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const SUBSCRIPTION_TIERS = [
    {
      id: 'price_1QjPiwGhjZlzWyxMiNhkIQTY',
      name: 'Base',
      dbTier: 'base' as const,
      price: '€4/' + t("account_settings.month", "month"),
      description: t("account_settings.base_product_description", "Perfect for getting started"),
      limit: 10,
    },
    {
      id: 'price_1QjPjQGhjZlzWyxMes9o9gJU',
      name: 'Business',
      dbTier: 'business' as const,
      price: '€9/' + t("account_settings.month", "month"),
      description: t("account_settings.business_product_description", "For growing businesses"),
      limit: 30,
    },
  ];
  const [selectedTier, setSelectedTier] = useState(SUBSCRIPTION_TIERS[0].id);
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    subscribed: boolean;
    subscription?: any;
  } | null>(null);

  const { data: profileData } = useQuery({
    queryKey: ['business-profile'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('business_profiles')
        .select('trial_status, trial_ends_at, subscription_tier')
        .eq('user_id', session?.user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!session,
  });

  const { data: usageData } = useQuery({
    queryKey: ['subscription-usage'],
    queryFn: async () => {
      const currentTier = profileData?.subscription_tier || 'base';

      const { data: limitsData, error: limitsError } = await supabase
        .from('subscription_limits')
        .select('*')
        .eq('tier', currentTier)
        .single();

      if (limitsError) throw limitsError;

      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { count, error: countError } = await supabase
        .from('invoice_schedules')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startOfMonth.toISOString());

      if (countError) throw countError;

      return {
        limit: limitsData.monthly_schedule_limit,
        used: count || 0,
        canSchedule: (count || 0) < limitsData.monthly_schedule_limit
      };
    },
    enabled: !!session,
  });

  const checkSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      if (error) throw error;
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error checking subscription:', error);
      toast.error(t("toasts.failed_check_subscription_status"));
    }
  };

  useEffect(() => {
    if (session) {
      checkSubscription();
    }
  }, [session]);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { priceId: selectedTier }
      });

      if (error) throw error;

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error(t("toasts.failed_to_start_subscription"));
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPlan = () => {
    if (!subscriptionStatus?.subscription?.items?.data[0]?.price?.id) return null;
    return SUBSCRIPTION_TIERS.find(
      tier => tier.id === subscriptionStatus.subscription.items.data[0].price.id
    );
  };

  const currentPlan = getCurrentPlan();
  const isTrialActive = profileData?.trial_status === 'active';
  const trialEndsAt = profileData?.trial_ends_at ? new Date(profileData.trial_ends_at) : null;
  const daysLeftInTrial = trialEndsAt ? Math.max(0, differenceInDays(trialEndsAt, new Date())) : 0;

  return (
    <div className="max-w-5xl mx-auto space-y-8 px-4 sm:px-6">
      <Card className="overflow-hidden border-0 shadow-lg">
        <CardContent className="p-4 sm:p-6 md:p-8 space-y-6 md:space-y-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6 pb-4 md:pb-6 border-b border-[#E2E8F0]">
            <div className="space-y-1 sm:space-y-2">
              <h3 className="text-xl sm:text-2xl font-semibold text-[#34375B] text-left">
                {currentPlan ? t("account_settings.your_current_plan", "Your Current Plan") : t("account_settings.trial_period", "Trial Period")}
              </h3>
              <p className="text-base sm:text-lg text-[#8791F3] text-left">
                {currentPlan?.name || t("account_settings.free_trial")}
              </p>
              {isTrialActive && trialEndsAt && (
                <p className="text-sm sm:text-base font-thin text-[#34375B]">
                  {t("account_settings.trial_ends_in", {
                    days: daysLeftInTrial,
                    date: format(trialEndsAt, 'MMMM do, yyyy')
                  })}
                </p>
              )}
            </div>

            {usageData && (
              <div className="bg-[#F1F0FB]/50 rounded-lg p-3 sm:p-4 w-full md:w-auto">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Gauge className="h-5 w-5 sm:h-6 sm:w-6 text-[#8791F3]" />
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-[#34375B]">
                      {t("account_settings.monthly_schedules", "Monthly Schedules")}
                    </p>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl sm:text-2xl font-bold text-[#8791F3]">{usageData.used}</span>
                      <span className="text-xs sm:text-sm text-gray-500">/ {usageData.limit}</span>
                    </div>
                  </div>
                </div>
                <Progress
                  value={(usageData.used / usageData.limit) * 100}
                  className="h-1.5 mt-2 sm:mt-3 bg-[#E2E8F0]"
                />
              </div>
            )}
          </div>

          <div className="space-y-4 sm:space-y-6">
            <h3 className="text-base sm:text-lg font-bold text-[#34375B] text-left">
              {subscriptionStatus?.subscribed
                ? t("account_settings.upgrade_plan", "Upgrade Your Plan")
                : t("account_settings.choose_plan", "Choose Your Plan")}
            </h3>

            <RadioGroup
              value={selectedTier}
              onValueChange={setSelectedTier}
              className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2"
            >
              {SUBSCRIPTION_TIERS.map((tier) => (
                <Card
                  key={tier.id}
                  className={`relative overflow-hidden transition-all duration-200 ${selectedTier === tier.id
                      ? 'border-[#8791F3] bg-[#F1F0FB]/30'
                      : 'border-[#E2E8F0] hover:border-[#8791F3]/50'
                    }`}
                >
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <RadioGroupItem value={tier.id} id={tier.id} className="mt-1" />
                      <Label htmlFor={tier.id} className="flex-1 cursor-pointer">
                        <div className="flex justify-between items-baseline mb-1 sm:mb-2">
                          <span className="text-sm sm:text-base font-semibold text-[#34375B]">{t(`pricing.tiers.${tier.name.toLowerCase()}.name`)}</span>
                          <span className="text-sm sm:text-base font-medium text-[#8791F3]">{tier.price}</span>
                        </div>
                        <div className="flex items-center text-xs sm:text-sm text-gray-600">
                          <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4 text-[#8791F3] mr-1 sm:mr-2 flex-shrink-0" />

                          {t("account_settings.monthly_invoice_limits", {
                            limit: tier.limit
                          })}
                        </div>
                      </Label>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </RadioGroup>

            <Button
              onClick={handleSubscribe}
              disabled={loading || (currentPlan?.id === selectedTier)}
              className="w-full sm:w-auto px-4 py-2 bg-[#8791F3] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200"
            >
              {loading ? (
                t("account_settings.processing", "Processing...")
              ) : currentPlan ? (
                <>
                  {t("account_settings.upgrade_now", "Upgrade Now")}
                </>
              ) : (
                <>
                  {t("account_settings.subscribe_now", "Subscribe Now")}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
